/**
 * Copyright (c) 2024-2026 Creative Agentics Limited. All rights reserved.
 * Proprietary and confidential. Unauthorised use, reproduction or distribution
 * of this software, in whole or in part, is strictly prohibited.
 * See LICENSE.txt for full terms.
 */

// Individual onboarding (pieces 10 and 11, 21/10/2026): the officer's card for
// an individual client, in place of the company panels' reading of a person.
// The lines are the Audit Record's (src/shared/individual-record.js), so the
// two say the same. The Swiss identification transfer is recorded here: the
// officer enters the first payment, and a payer who is not the client is said.
import React, { useState } from 'react';
import { individualRecordLines, identificationTransferState } from './shared/individual-record.js';

export default function IndividualPersonCard({ report, collected, formRequests, formAState, features, samePerson, onRecordTransfer, canRecord }) {
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState({ payerName: '', bank: '', country: '', accountRef: '', paidAt: '', amount: '', currency: '' });
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState(null);
  const lines = individualRecordLines({ report, collected, formRequests, formAState, features, samePerson });
  const tr = identificationTransferState({ collected, report, features, formAState, samePerson });
  const attn = (k, v) => (/Not recorded|not confirmed|is not the client|Awaiting|could not|not in the client/i.test(v) ? 'var(--lc9)' : 'var(--lc4)');
  const field = { width: '100%', padding: '7px 10px', background: 'var(--lc25)', border: '1px solid var(--lc30)', borderRadius: 6, color: 'var(--lc7)', fontSize: 12, fontFamily: 'inherit', boxSizing: 'border-box' };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  return (
    <div data-individual-person-card style={{ background: 'var(--lc18)', border: '1px solid var(--lc27)', borderRadius: 12, marginBottom: 12, padding: '14px 20px' }}>
      <div style={{ fontSize: 11, color: 'var(--lc2)', textTransform: 'uppercase', letterSpacing: '0.8px', marginBottom: 10 }}>Individual client</div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(92px, min(190px, 34%)) 1fr', gap: '6px 12px' }}>
        {lines.map(([k, v]) => (
          <React.Fragment key={k}>
            <div style={{ fontSize: 11.5, color: 'var(--lc14)' }}>{k}</div>
            <div style={{ fontSize: 12, color: attn(k, v), lineHeight: 1.5, overflowWrap: 'anywhere' }}>{v}</div>
          </React.Fragment>
        ))}
      </div>
      {tr.applies && !tr.matches && canRecord && (
        <div style={{ marginTop: 12, paddingTop: 10, borderTop: '1px solid var(--lc6)' }}>
          {!open ? (
            <button onClick={() => setOpen(true)} style={{ background: 'transparent', border: '1px solid var(--lc29)', color: 'var(--lc4)', borderRadius: 6, padding: '6px 12px', fontSize: 12, cursor: 'pointer' }}>Record the identification transfer</button>
          ) : (
            <div data-id-transfer-form>
              <div style={{ fontSize: 11, color: 'var(--lc14)', marginBottom: 6, lineHeight: 1.5 }}>The first payment received from the client. Enter the payer exactly as the bank shows it; only the last four characters of the account are kept.</div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 6 }}>
                <input aria-label="Payer name" placeholder="Payer name as the bank shows it" value={form.payerName} onChange={set('payerName')} style={{ ...field, flex: '2 1 200px' }} />
                <input aria-label="Bank" placeholder="Bank" value={form.bank} onChange={set('bank')} style={{ ...field, flex: '1 1 120px' }} />
                <input aria-label="Country of the bank" placeholder="Country of the bank" value={form.country} onChange={set('country')} style={{ ...field, flex: '1 1 120px' }} />
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 8 }}>
                <input aria-label="Account" placeholder="IBAN or account number" value={form.accountRef} onChange={set('accountRef')} style={{ ...field, flex: '2 1 200px' }} />
                <input aria-label="Date received" type="date" value={form.paidAt} onChange={set('paidAt')} style={{ ...field, flex: '1 1 130px' }} />
                <input aria-label="Amount" placeholder="Amount" value={form.amount} onChange={set('amount')} style={{ ...field, flex: '1 1 90px' }} />
                <input aria-label="Currency" placeholder="CHF" maxLength={3} value={form.currency} onChange={set('currency')} style={{ ...field, flex: '0 1 70px' }} />
              </div>
              {err && <div style={{ fontSize: 11.5, color: 'var(--lc92)', marginBottom: 6 }}>{err}</div>}
              <div style={{ display: 'flex', gap: 8 }}>
                <button disabled={busy} onClick={async () => {
                  if (!form.payerName.trim() || !form.paidAt) { setErr('Enter the payer name and the date received.'); return; }
                  setBusy(true); setErr(null);
                  try { await onRecordTransfer(form); setOpen(false); }
                  catch (e) { setErr((e && e.message) || 'The transfer could not be recorded. Nothing was saved.'); }
                  setBusy(false);
                }} style={{ background: 'var(--lc8)', border: 'none', color: 'var(--lc18)', borderRadius: 6, padding: '7px 14px', fontSize: 12, fontWeight: 600, cursor: busy ? 'default' : 'pointer', opacity: busy ? 0.5 : 1 }}>Record</button>
                <button onClick={() => setOpen(false)} style={{ background: 'transparent', border: '1px solid var(--lc29)', color: 'var(--lc4)', borderRadius: 6, padding: '7px 12px', fontSize: 12, cursor: 'pointer' }}>Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
